import React, { useState } from 'react';
import { Card, Table, Button, Tag, Typography, Modal, Form, Input, Select, Space } from 'antd';
import { ExperimentOutlined, PlusOutlined, PlayCircleOutlined, PauseCircleOutlined } from '@ant-design/icons';

const { Title } = Typography;
const { Option } = Select;

const ModelManagement: React.FC = () => {
    const [modalOpen, setModalOpen] = useState(false);

    const models = [
        {
            id: '1',
            name: '风电场A-1号机组预测模型',
            dataset: '风电场A-1号机组 - 历史数据 (25920点)',
            baseModel: '大源模型',
            relatedSite: '风电场A-1号机组',
            path: '/models/wind_a_01_model.pkl',
            fileSize: '125.6MB',
            accuracy: 95.2,
            status: 'running'
        },
        {
            id: '2',
            name: '光伏场B-1号方阵预测模型',
            dataset: '光伏场B-1号方阵 - 历史数据 (12960点)',
            baseModel: '大源模型',
            relatedSite: '光伏场B-1号方阵',
            path: '/models/solar_b_01_model.pkl',
            fileSize: '98.3MB',
            accuracy: 93.8,
            status: 'running'
        },
        {
            id: '3',
            name: '水电站C-1号机组预测模型',
            dataset: '水电站C-1号机组 - 历史数据 (25920点)',
            baseModel: '大源模型',
            relatedSite: '水电站C-1号机组',
            path: '/models/hydro_c_01_model.pkl',
            fileSize: '45.2MB',
            accuracy: 89.5,
            status: 'stopped'
        },
        {
            id: '4',
            name: '风电场A-2号机组预测模型',
            dataset: '风电场A-2号机组 - 历史数据 (25920点)',
            baseModel: '大源模型',
            relatedSite: '风电场A-2号机组',
            path: '/models/wind_a_02_model.pkl',
            fileSize: '156.8MB',
            accuracy: 91.2,
            status: 'training'
        },
    ];

    // 历史数据集选项
    const historicalDatasets = [
        {
            value: 'dataset1',
            label: '风电场A-1号机组 - 历史数据 (25920点)',
            site: '风电场A-1号机组',
            dataType: '风力发电',
            dataPoints: 25920,
            fileSize: '222.2MB',
            storageDir: '/data/wind/station_a_01/historical/'
        },
        {
            value: 'dataset2',
            label: '风电场A-2号机组 - 历史数据 (25920点)',
            site: '风电场A-2号机组',
            dataType: '风力发电',
            dataPoints: 25920,
            fileSize: '222.2MB',
            storageDir: '/data/wind/station_a_02/historical/'
        },
        {
            value: 'dataset3',
            label: '光伏场B-1号方阵 - 历史数据 (12960点)',
            site: '光伏场B-1号方阵',
            dataType: '光伏发电',
            dataPoints: 12960,
            fileSize: '217.3MB',
            storageDir: '/data/solar/station_b_01/historical/'
        },
        {
            value: 'dataset4',
            label: '水电站C-1号机组 - 历史数据 (25920点)',
            site: '水电站C-1号机组',
            dataType: '水力发电',
            dataPoints: 25920,
            fileSize: '180.3MB',
            storageDir: '/data/hydro/station_c_01/historical/'
        },
    ];

    const getStatusColor = (status: string) => {
        const statusMap = {
            running: 'green',
            stopped: 'red',
            training: 'blue'
        };
        return statusMap[status as keyof typeof statusMap];
    };

    const getStatusText = (status: string) => {
        const statusMap = {
            running: '运行中',
            stopped: '已停止',
            training: '训练中'
        };
        return statusMap[status as keyof typeof statusMap];
    };

    const columns = [
        {
            title: '模型名称',
            dataIndex: 'name',
            key: 'name',
            width: 200,
        },
        {
            title: '数据集',
            dataIndex: 'dataset',
            key: 'dataset',
            width: 180,
        },
        {
            title: '基础模型',
            dataIndex: 'baseModel',
            key: 'baseModel',
            width: 120,
        },
        {
            title: '关联站点',
            dataIndex: 'relatedSite',
            key: 'relatedSite',
            width: 150,
        },
        {
            title: '存储路径',
            dataIndex: 'path',
            key: 'path',
            width: 200,
        },
        {
            title: '模型文件大小',
            dataIndex: 'fileSize',
            key: 'fileSize',
            width: 120,
        },
        {
            title: '预测精度',
            dataIndex: 'accuracy',
            key: 'accuracy',
            width: 150,
            render: (accuracy: number) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{
                        width: '60px',
                        height: '8px',
                        background: '#f0f0f0',
                        borderRadius: '4px',
                        marginRight: '8px'
                    }}>
                        <div style={{
                            width: `${accuracy}%`,
                            height: '100%',
                            background: '#1e88e5',
                            borderRadius: '4px'
                        }} />
                    </div>
                    {accuracy}%
                </div>
            ),
        },
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (status: string) => (
                <Tag color={getStatusColor(status)}>
                    {getStatusText(status)}
                </Tag>
            ),
        },
        {
            title: '操作',
            key: 'action',
            width: 200,
            render: (_: any, record: any) => (
                <Space size="small">
                    {record.status === 'running' ? (
                        <Button icon={<PauseCircleOutlined />} size="small" />
                    ) : (
                        <Button icon={<PlayCircleOutlined />} size="small" />
                    )}
                    <Button size="small">重新训练</Button>
                    <Button size="small" danger>删除</Button>
                </Space>
            ),
        },
    ];

    return (
        <div className="page-transition">
            <Card>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <Title level={2} style={{ margin: 0 }}>
                        <ExperimentOutlined style={{ marginRight: '8px' }} />
                        模型管理
                    </Title>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalOpen(true)}>
                        新增模型
                    </Button>
                </div>

                <Table
                    columns={columns}
                    dataSource={models}
                    rowKey="id"
                    pagination={false}
                    scroll={{ x: 1400 }}
                />
            </Card>

            <Modal
                title="新增预测模型"
                open={modalOpen}
                onCancel={() => setModalOpen(false)}
                footer={[
                    <Button key="cancel" onClick={() => setModalOpen(false)}>取消</Button>,
                    <Button key="submit" type="primary" onClick={() => setModalOpen(false)}>开始训练</Button>
                ]}
                width={600}
            >
                <Form layout="vertical">
                    <Form.Item label="模型名称" rules={[{ required: true, message: '请输入模型名称' }]}>
                        <Input placeholder="请输入模型名称" />
                    </Form.Item>

                    <Form.Item label="数据集" rules={[{ required: true, message: '请选择数据集' }]}>
                        <Select placeholder="从历史数据管理中选择数据集">
                            {historicalDatasets.map(dataset => (
                                <Option key={dataset.value} value={dataset.value}>
                                    {dataset.label}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item label="基础模型" rules={[{ required: true, message: '请选择基础模型' }]}>
                        <Select placeholder="请选择基础模型类型">
                            <Option value="大源模型">大源模型</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item label="存储路径">
                        <Input placeholder="模型文件存储路径 (可选，系统将自动生成)" />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default ModelManagement; 